import { Link, useNavigate } from 'react-router-dom'
import { MapPin, Lock, AlertCircle, Pencil, Plus, Zap } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { formatCurrency, formatDate } from '@/lib/format'
import type { ClientProjectGroupV2, ClientProjectItemV2 } from '@/lib/api/v2/clients'

function getMilestoneColorClasses(status?: string | null): string {
  const statusLower = (status || '').toLowerCase()
  
  switch (statusLower) {
    case 'danger':
      return 'bg-red-100 text-red-600 hover:bg-red-100'
    case 'active':
      // A fill, like every sibling status in this map. The outlined version introduced with the
      // ink theme was white on a white card — and its `border border-slate-300` never rendered at
      // all, because both call sites pass `border-0` on the Badge and that wins in the emitted
      // stylesheet. So the one status meaning « the client is HERE right now » had literally no
      // boundary of any kind.
      //
      // slate-200 + slate-900, and this is a compromise between two findings that are BOTH right.
      // A first pass tried solid ink and the browser QA showed why 432f36a had moved away from it:
      // twenty black pills in a card grid outweigh the amber profile warning above them, which is
      // the one thing on this page that is supposed to shout. But a neutral chip cannot reach the
      // 3:1 a component boundary wants without going dark, and dark is what that commit rejected.
      // So: one clear step above `default` (bg-slate-100 text-slate-600), separated by fill AND by
      // text weight, quiet enough not to dominate. The real answer is a hue this map does not
      // spend yet — that is a design decision, not a review fix.
      return 'bg-slate-200 text-slate-900 hover:bg-slate-200'
    case 'info':
      return 'bg-sky-100 text-sky-600 hover:bg-sky-100'
    case 'green':
      return 'bg-green-100 text-green-600 hover:bg-green-100'
    case 'warn':
      return 'bg-amber-100 text-amber-600 hover:bg-amber-100'
    default:
      return 'bg-slate-100 text-slate-600 hover:bg-slate-100'
  }
}

function calcTopStats(groups: ClientProjectGroupV2[]) {
  //TODO get from the back
  const chantierCount = groups.length
  const allProjects = groups.flatMap((g) => g.projects || [])
  const projectCount = allProjects.length

  const inProgress = allProjects.length

  return { chantierCount, projectCount, inProgress }
}

function ProjectCard({ project }: { project: ClientProjectItemV2 }) {
  const reference = project.reference?.trim() || ''
  const name = project.name?.trim() || 'Projet sans nom'

  const tags = [project.typeLabel?.trim() || project.type?.trim()].filter(Boolean) as string[]

  const milestones = (project.milestones || []).filter(Boolean)

  const hasQuoteAccess = project.quote?.hasAccess ?? false
  const hasInvoicingAccess = project.invoicing?.hasAccess ?? false

  const isQuoteAccepted = project.quote?.isQuoteAccepted ?? false
  const quoteTotalWithTax = hasQuoteAccess ? project.quote?.totals?.totalWithTax : null
  const salesOrderTotal = project.quote?.totals?.salesOrderTotalWithTax ?? null
  const quoteLabel = isQuoteAccepted ? 'Commande' : 'Devis'
  const displayAmount = isQuoteAccepted ? salesOrderTotal : quoteTotalWithTax
  const invoicingSummary = hasInvoicingAccess ? project.invoicing?.summary : null

  return (
    <Card className="p-4 shadow-sm hover:shadow-md transition-shadow border-border/50">
      <Link to={`/projects/${project.projectId}`} className="block">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {reference && <span className="font-medium">{reference}</span>}
              {project.isDirectSale ? (
                <Badge className="rounded-md border-0 bg-indigo-100 text-indigo-700">
                  Vente directe
                </Badge>
              ) : (
                tags.slice(0, 2).map((t) => (
                  <Badge key={t} variant="outline" className="rounded-sm">
                    {t}
                  </Badge>
                ))
              )}
            </div>

            <h3 className="mt-2 font-semibold text-base leading-snug">{name}</h3>

            {milestones.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {milestones.slice(0, 4).map((m, idx) => {
                  const colorClasses = getMilestoneColorClasses(m.status)
                  return (
                    <Badge key={`${m.label || ''}-${idx}`} variant="outline" className={`rounded-md border-0 ${colorClasses}`}>
                      {m.label || m.status || '—'}
                    </Badge>
                  )
                })}
              </div>
            )}
          </div>

          <div className="text-right text-xs text-muted-foreground shrink-0">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="uppercase tracking-wide">{quoteLabel}</div>
                {!hasQuoteAccess ? (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <Lock className="h-3 w-3" />
                    <span>—</span>
                  </div>
                ) : (
                  <div className="text-base font-bold text-foreground">
                    {displayAmount != null ? formatCurrency(displayAmount) : '—'}
                  </div>
                )}
              </div>
              <div>
                <div className="uppercase tracking-wide">Facturé</div>
                {!hasInvoicingAccess ? (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <Lock className="h-3 w-3" />
                    <span>—</span>
                  </div>
                ) : (
                  <div className="text-base font-bold text-foreground">
                    {invoicingSummary?.amountInvoiced != null
                      ? formatCurrency(invoicingSummary.amountInvoiced)
                      : '—'}
                  </div>
                )}
              </div>
            </div>

            {hasInvoicingAccess && (invoicingSummary?.percentageInvoiced ?? null) != null && (
              <div className="mt-1">{invoicingSummary?.percentageInvoiced}%</div>
            )}

            {hasInvoicingAccess && invoicingSummary?.lastInvoiceDate && (
              <div className="mt-1">{formatDate(invoicingSummary.lastInvoiceDate)}</div>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            {project.salesOwner?.initials && (
              <Badge variant="outline" className="rounded-full px-2">
                {project.salesOwner.initials}
              </Badge>
            )}
            {project.salesOwner?.fullName && <span>{project.salesOwner.fullName}</span>}
          </div>

          {project.defaultVat != null && <span>TVA {Math.round(project.defaultVat * 100)}%</span>}
        </div>
      </Link>
    </Card>
  )
}

interface ClientProjectsByJobSiteProps {
  groups: ClientProjectGroupV2[]
  customerId: string
  hasBillingAddress: boolean
}

export function ClientProjectsByJobSite({ groups, customerId, hasBillingAddress }: ClientProjectsByJobSiteProps) {
  const navigate = useNavigate()
  const { chantierCount, projectCount, inProgress } = calcTopStats(groups)

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Chantiers & Projets</h2>
        <p className="text-sm text-muted-foreground">
          {chantierCount} chantiers · {projectCount} projets
          {inProgress > 0 ? ` en cours` : ''}
        </p>
      </div>

      <div className="space-y-8">
        {groups.map((g, idx) => {
          const jobSite = g.jobSite
          const projects = (g.projects || []).filter(Boolean)
          const jsTitle = jobSite?.label?.trim() || 'Chantier'
          const jsRef = jobSite?.reference?.trim() || ''
          const jsAddress = jobSite?.address?.trim() || ''

          return (
            <div key={jobSite?.id || idx} className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="mt-1 text-foreground">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-semibold truncate">{jsTitle}</h3>
                      {jsRef && (
                        <Badge variant="outline" className="text-xs">{jsRef}</Badge>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      {jobSite?.id ? (
                        <>
                          {jsAddress ? (
                            <p className="text-sm text-muted-foreground truncate">{jsAddress}</p>
                          ) : (
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                              <span className="text-xs italic">Adresse non définie</span>
                            </div>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 shrink-0"
                            onClick={() => navigate(`/job-sites/${jobSite.id}/edit`, { state: { from: window.location.pathname } })}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                            <span className="text-xs italic">Adresse non définie</span>
                          </div>
                          {projects.length > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => {
                                const firstProjectId = projects[0].projectId
                                navigate(`/projects/${firstProjectId}/job-site/new`, { 
                                  state: { 
                                    from: window.location.pathname,
                                    customerId,
                                    hasBillingAddress
                                  } 
                                })
                              }}
                            >
                              Créer l'adresse
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="secondary" className="rounded-full font-medium">
                    {projects.length} projet{projects.length > 1 ? 's' : ''}
                  </Badge>
                  {jobSite?.id && (
                    <>
                      <Button
                        data-testid="jobsite-new-order-btn"
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1.5 text-xs"
                        onClick={() =>
                          navigate(
                            `/projects/new?customerId=${customerId}&jobSiteId=${jobSite.id}&step=3`
                          )
                        }
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Nouvelle commande
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1.5 text-xs"
                        onClick={() =>
                          navigate(
                            `/projects/new?customerId=${customerId}&jobSiteId=${jobSite.id}&step=3&mode=direct-sale`
                          )
                        }
                      >
                        <Zap className="h-3.5 w-3.5" />
                        Vente directe
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {projects.map((p) => (
                  <ProjectCard key={p.projectId} project={p} />
                ))}
              </div>

              {idx < groups.length - 1 && <Separator />}
            </div>
          )
        })}
      </div>
    </div>
  )
}
